/**
 * TeamLeaderboard Component
 * Ranked list showing top performing teams/departments
 *
 * Story 2.4: Top Performing Teams Leaderboard
 */

import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { Trophy, TrendingUp, Users, Target } from 'lucide-react';
import { Card, Badge } from '../../common';

export interface TeamLeaderboardEntry {
  department: string;
  /** Department uuid, or null for the "No department" row. */
  departmentId: string | null;
  okrCount: number;
  averageProgress: number;
  completionRate: number;
  previousProgress?: number;
}

export interface TeamLeaderboardProps {
  /** Team/department data */
  data: TeamLeaderboardEntry[];
  /** Title for the leaderboard */
  title?: string;
  /** Maximum number of entries to show */
  limit?: number;
  /** Minimum OKR count to appear on leaderboard */
  minOKRCount?: number;
  /** Click handler for team row; null for the "No department" row */
  onTeamClick?: (departmentId: string | null) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

type SortMetric = 'progress' | 'completionRate';

/**
 * Get rank badge styling based on position
 */
function getRankBadge(rank: number): { bg: string; text: string; icon?: React.ReactNode } {
  switch (rank) {
    case 1:
      return { bg: 'bg-amber-100', text: 'text-amber-700', icon: <Trophy className="w-4 h-4" /> };
    case 2:
      return { bg: 'bg-stone-200', text: 'text-stone-700' };
    case 3:
      return { bg: 'bg-orange-100', text: 'text-orange-700' };
    default:
      return { bg: 'bg-stone-100', text: 'text-stone-600' };
  }
}

/**
 * Get progress bar color based on value
 */
function getProgressColor(progress: number): string {
  if (progress >= 70) return 'bg-green-500';
  if (progress >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Loading skeleton
 */
const LeaderboardSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
        <div className="w-8 h-8 rounded-full bg-stone-200" />
        <div className="flex-1">
          <div className="h-4 bg-stone-200 rounded w-32 mb-2" />
          <div className="h-2 bg-stone-200 rounded w-full" />
        </div>
        <div className="h-4 bg-stone-200 rounded w-12" />
      </div>
    ))}
  </div>
);

/**
 * Team Leaderboard Component
 */
export const TeamLeaderboard: React.FC<TeamLeaderboardProps> = ({
  data,
  title = 'Top Performing Teams',
  limit = 10,
  minOKRCount = 3,
  onTeamClick,
  isLoading = false,
  className,
}) => {
  const [sortMetric, setSortMetric] = useState<SortMetric>('progress');

  // Filter and sort data
  const rankedData = useMemo(() => {
    return data
      .filter((entry) => entry.okrCount >= minOKRCount)
      .sort((a, b) => {
        if (sortMetric === 'progress') {
          return b.averageProgress - a.averageProgress;
        }
        return b.completionRate - a.completionRate;
      })
      .slice(0, limit);
  }, [data, sortMetric, limit, minOKRCount]);

  // Find the team with highest improvement
  const mostImprovedIndex = useMemo(() => {
    let maxImprovement = -Infinity;
    let index = -1;
    rankedData.forEach((entry, i) => {
      if (entry.previousProgress !== undefined) {
        const improvement = entry.averageProgress - entry.previousProgress;
        if (improvement > maxImprovement) {
          maxImprovement = improvement;
          index = i;
        }
      }
    });
    return maxImprovement > 0 ? index : -1;
  }, [rankedData]);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <LeaderboardSkeleton rows={5} />
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <div className="flex flex-col items-center justify-center py-12 text-stone-500">
          <Users className="w-12 h-12 mb-3 text-stone-300" />
          <p>No team data available</p>
        </div>
      </Card>
    );
  }

  if (rankedData.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <div className="flex flex-col items-center justify-center py-12 text-stone-500">
          <Target className="w-12 h-12 mb-3 text-stone-300" />
          <p>No teams meet the minimum OKR count ({minOKRCount})</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-900">{title}</h3>

        {/* Sort toggle */}
        <div className="flex gap-1 bg-stone-100 rounded-lg p-1">
          <button
            onClick={() => setSortMetric('progress')}
            className={clsx(
              'px-3 py-1 text-sm rounded-md transition-colors',
              sortMetric === 'progress'
                ? 'bg-white text-stone-900 shadow-sm'
                : 'text-stone-600 hover:text-stone-900'
            )}
          >
            By Progress
          </button>
          <button
            onClick={() => setSortMetric('completionRate')}
            className={clsx(
              'px-3 py-1 text-sm rounded-md transition-colors',
              sortMetric === 'completionRate'
                ? 'bg-white text-stone-900 shadow-sm'
                : 'text-stone-600 hover:text-stone-900'
            )}
          >
            By Completion
          </button>
        </div>
      </div>

      {/* Leaderboard entries */}
      <div className="space-y-2">
        {rankedData.map((entry, index) => {
          const rank = index + 1;
          const rankStyle = getRankBadge(rank);
          const isMostImproved = index === mostImprovedIndex;
          const metricValue = sortMetric === 'progress' ? entry.averageProgress : entry.completionRate;

          return (
            <div
              key={entry.department}
              onClick={() => onTeamClick?.(entry.departmentId)}
              className={clsx(
                'flex items-center gap-4 p-3 rounded-lg transition-colors',
                onTeamClick ? 'cursor-pointer hover:bg-stone-50' : '',
                isMostImproved && 'ring-2 ring-green-200 bg-green-50'
              )}
            >
              {/* Rank badge */}
              <div
                className={clsx(
                  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm',
                  rankStyle.bg,
                  rankStyle.text
                )}
              >
                {rankStyle.icon || rank}
              </div>

              {/* Team info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-stone-900 truncate">
                    {entry.department}
                  </span>
                  {isMostImproved && (
                    <Badge variant="success" size="sm" className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Most Improved
                    </Badge>
                  )}
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full transition-all duration-500', getProgressColor(metricValue))}
                      style={{ width: `${metricValue}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-stone-700 w-12 text-right">
                    {metricValue}%
                  </span>
                </div>

                {/* Additional stats */}
                <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
                  <span>{entry.okrCount} OKRs</span>
                  <span>
                    {sortMetric === 'progress' ? `${entry.completionRate}% completed` : `${entry.averageProgress}% progress`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      {data.length > rankedData.length && (
        <p className="text-xs text-stone-400 mt-4 text-center">
          Showing top {rankedData.length} of {data.filter((d) => d.okrCount >= minOKRCount).length} teams
          (min. {minOKRCount} OKRs required)
        </p>
      )}
    </Card>
  );
};

export default TeamLeaderboard;
